/**
 * 캘린더 모듈 도메인 모델 — Phase 1 (visibility 모델)
 *
 * 합의 (2026-05-21):
 * - event_type 폐기 (추후 추가 속성과 함께 부활)
 * - scope/channel_id/mention_* 폐기
 * - visibility 도입: private / public_channels / public_workspace
 * - shared_channel_ids: 다중 채널 공개 대상
 * - 초대(event_attendees) 별개 축. RSVP 4종.
 * - 게스트: "채널 일정"(public_channels) 만 생성/관리 가능 — 개인·전사 일정은 차단. 채널 멤버십 전제.
 */

/** 일정 작성자 (임베디드 JSON) */
export interface EventAuthor {
  uid: string;
  name: string;
  photoUrl?: string | null;
}

/** 일정 가시성 — 공개 범위 */
export type EventVisibility =
  | 'private'             // 본인만
  | 'public_channels'     // 선택한 채널 멤버들 + 초대받은 사람
  | 'public_workspace';   // 워크스페이스 전체 (admin 전용)

// 라벨은 컴포넌트에서 useCalendarLabels().visibility() 로 i18n (구 VISIBILITY_LABELS 제거)

/** 일정 상태 */
export type EventStatus = 'confirmed' | 'tentative' | 'cancelled';

/** 참석자 응답 상태 */
export type AttendeeStatus = 'pending' | 'accepted' | 'declined' | 'tentative';

// 라벨은 컴포넌트에서 useCalendarLabels().attendeeStatus() 로 i18n (구 ATTENDEE_STATUS_LABELS 제거)

/** 캘린더 알림 타입 */
export type EventNotificationType =
  | 'invitation'         // 초대 받음
  | 'rsvp_response'      // 초대한 사람에게 응답 알림
  | 'event_updated'      // 일정 시간/장소/제목 변경
  | 'event_cancelled';   // 일정 취소

// 알림 타입 라벨은 통지 센터에서 메시지로 처리 (구 EVENT_NOTIFICATION_LABELS 제거)

// ─── 캘린더 뷰 모드 ────────────────────────────────────────

export type CalendarViewMode = 'month' | 'week' | 'day' | 'agenda';

const CALENDAR_VIEW_PREF_KEY = 'calendar-view-mode';

export function getUserCalendarView(): CalendarViewMode | null {
  try {
    const v = localStorage.getItem(CALENDAR_VIEW_PREF_KEY);
    if (v === 'month' || v === 'week' || v === 'day' || v === 'agenda') return v;
    return null;
  } catch { return null; }
}

export function setUserCalendarView(mode: CalendarViewMode): void {
  try { localStorage.setItem(CALENDAR_VIEW_PREF_KEY, mode); } catch {}
}

// ─── 필터 토글 ────────────────────────────────────────────

const FILTER_PREF_KEY = 'calendar-filters';

/**
 * 사이드바 필터 토글 상태 — 3 분류 (전사/채널/개인) + 시스템 (2026-05-22)
 *
 * 구조:
 *   workspace          전사 일정 — 단일 토글
 *   personal           개인 일정 (본인 소유 + owner_channel_id=null)
 *   channels[id]       각 채널의 일정 보기 여부 (기본 true)
 *   showChannelTasks   채널 그룹 전역 — Task 마감일 표시 여부 (마스터). ON 일 때만 채널 토글에 종속해 함께 표시.
 *   holidays           시스템 공휴일
 *
 * 분류 우선순위 (shouldShowEvent):
 *   1. metadata.__holiday → holidays
 *   2. metadata.__task   → showChannelTasks && channels[task.channel_id]
 *   3. visibility=public_workspace → workspace
 *   4. owner_id==me && owner_channel_id==null → personal
 *   5. owner_channel_id ∈ channels → channels[owner_channel_id]
 *   6. shared_channel_ids 중 사용자 채널 첫 매칭 → channels[matched]
 *   7. 그 외 → false
 */
export interface CalendarFilters {
  workspace: boolean;
  /** 개인 일정 — 본인이 작성한 owner_channel_id=null 일정 전부 (채널에 공유했든 안 했든) */
  personal: boolean;
  channels: Record<string, boolean>;
  /** 채널 그룹 마스터 — 다른 사람의 공개된 일정 (그들의 개인 일정이 내 채널에 공유됨) 표시 여부. ON 일 때만 채널 토글 종속 */
  showSharedEvents: boolean;
  /** 채널 그룹 마스터 — Task 마감일 표시. ON 일 때만 채널 토글 종속 */
  showChannelTasks: boolean;
  holidays: boolean;
  /** 개인이 선택한 공휴일 국가 코드(per-user). null = 아직 미선택 → 로케일 기본(localeDefaultHolidayCountries). 배열이면 그 국가만 */
  holidayCountries: string[] | null;
}

export const DEFAULT_FILTERS: CalendarFilters = {
  workspace: true,
  personal: true,
  channels: {},
  showSharedEvents: true,
  showChannelTasks: true,
  holidays: true,
  holidayCountries: null,
};

/**
 * 로케일 → 기본 공휴일 국가. 개인이 국가를 직접 고르기 전(holidayCountries=null) 적용.
 * 시드된 KR/JP 기준 — 그 외 로케일은 빈 배열(사용자가 직접 선택할 때까지 표시 안 함).
 */
export function localeDefaultHolidayCountries(locale: string): string[] {
  const lang = (locale || '').split(/[-_]/)[0].toLowerCase();
  if (lang === 'ja') return ['JP'];
  if (lang === 'ko') return ['KR'];
  return [];
}

/** filters.holidayCountries(null=로케일 기본) 를 실제 표시 국가 배열로 해석 */
export function resolveHolidayCountries(
  holidayCountries: string[] | null,
  locale: string,
): string[] {
  return holidayCountries ?? localeDefaultHolidayCountries(locale);
}

// 국가명 라벨은 useCalendarLabels().country() (Intl.DisplayNames, locale 자동) 사용
// — 구 countryLabel/COUNTRY_LABELS(한글 고정) 제거

/** 특정 채널의 표시 여부 — 미설정이면 기본 true */
export function isChannelEnabled(filters: CalendarFilters, channelId: string): boolean {
  return filters.channels[channelId] !== false;
}

export function loadCalendarFilters(): CalendarFilters {
  try {
    const raw = localStorage.getItem(FILTER_PREF_KEY);
    if (!raw) return DEFAULT_FILTERS;
    const parsed = JSON.parse(raw);
    return {
      workspace: parsed.workspace ?? DEFAULT_FILTERS.workspace,
      personal: parsed.personal ?? DEFAULT_FILTERS.personal,
      channels: typeof parsed.channels === 'object' && parsed.channels ? parsed.channels : {},
      showSharedEvents: parsed.showSharedEvents ?? DEFAULT_FILTERS.showSharedEvents,
      showChannelTasks: parsed.showChannelTasks ?? DEFAULT_FILTERS.showChannelTasks,
      holidays: parsed.holidays ?? DEFAULT_FILTERS.holidays,
      holidayCountries: Array.isArray(parsed.holidayCountries) ? parsed.holidayCountries : null,
    };
  } catch { return DEFAULT_FILTERS; }
}

export function saveCalendarFilters(filters: CalendarFilters): void {
  try { localStorage.setItem(FILTER_PREF_KEY, JSON.stringify(filters)); } catch {}
}

// ─── 알림 진입 안내 dismiss 추적 (localStorage) ──────────────

const INVITATION_TOAST_DISMISS_KEY = 'calendar-invitation-toast-dismissed-at';

/** 마지막으로 토스트를 닫은(또는 확인한) 시각을 저장 */
export function setInvitationToastDismissed(): void {
  try { localStorage.setItem(INVITATION_TOAST_DISMISS_KEY, new Date().toISOString()); } catch {}
}

/** 토스트 다시 띄울지 판단 — 마지막 dismiss 이후 새 초대가 있는지 */
export function shouldShowInvitationToast(latestInvitationCreatedAt: string | null): boolean {
  if (!latestInvitationCreatedAt) return false;
  try {
    const dismissed = localStorage.getItem(INVITATION_TOAST_DISMISS_KEY);
    if (!dismissed) return true;
    return new Date(latestInvitationCreatedAt) > new Date(dismissed);
  } catch { return true; }
}

// ─── 권한 체크 ────────────────────────────────────────────

type RoleLike = 'owner' | 'admin' | 'member' | 'guest';

/**
 * 일정을 어떤 visibility 로 만들 수 있는가 (UI 분기용)
 * - 게스트: "채널 일정"(public_channels) 만 — 개인(private)·전사(public_workspace) 차단.
 *   게스트는 채널 캘린더 컨텍스트(forceChannel)로만 모달에 진입하므로 실제로도 public_channels 로 귀결.
 * - 일반 멤버: private / public_channels
 * - admin/owner: 모두 (public_workspace 포함)
 */
export function allowedVisibilities(role: RoleLike): EventVisibility[] {
  if (role === 'guest') return ['public_channels'];
  if (role === 'owner' || role === 'admin') {
    return ['private', 'public_channels', 'public_workspace'];
  }
  return ['private', 'public_channels'];
}

/** 일정 수정/삭제 가능 여부
 * - owner_id 본인 → 항상 가능 (게스트가 만든 채널 일정도 본인 소유이므로 관리 가능)
 * - owner_channel_id 가 있고 그 채널의 admin/owner → 가능
 * - public_workspace + workspace admin → 가능
 */
export function canManageEvent(
  event: Pick<CalendarEvent, 'visibility' | 'owner_id' | 'owner_channel_id'>,
  role: RoleLike,
  userId: string,
  isChannelAdminOf?: (channelId: string) => boolean,
): boolean {
  if (event.owner_id === userId) return true;
  if (event.owner_channel_id && isChannelAdminOf?.(event.owner_channel_id)) return true;
  if (event.visibility === 'public_workspace' && (role === 'owner' || role === 'admin')) return true;
  return false;
}

/** 일정의 분류 (UI 라벨/시각 분기용) */
export type EventCategory =
  | 'private'              // 비공개 개인
  | 'private_shared'       // 공유한 개인 일정 (owner=me + shared 있음, owner_channel_id=null)
  | 'channel_mine'         // 본인이 속한 채널의 일정
  | 'channel_shared'       // 다른 채널 일정이 내 채널에 공유됨 — 채널 캘린더 안에서만 의미
  | 'received'             // 다른 사람이 만든 일정 (메인 캘린더에서)
  | 'invited'              // 초대받음 (attendee)
  | 'workspace'            // 전사 일정
  | 'holiday'              // 공휴일 (가상)
  | 'task';                // Tasks 마감일 (가상, posts.metadata.due_date 기반)

/** 카테고리별 chip 시각 스타일 — 모든 뷰에서 공통 사용 */
export interface EventChipStyle {
  /** 종일 막대 / 일 뷰 큰 막대 배경 (text 색은 white) */
  filledBg: string;
  /** 시간 지정 chip dot 색상 */
  dot: string;
  /** 어젠다 카드 배경 (옅은) */
  softBg: string;
  /** 추가 border (공유받은 일정 dashed 등) */
  borderExtra?: string;
  /** 카테고리 아이콘 — 모든 chip 왼쪽에 표시 */
  iconKey: ChipMarkerKey;
  /** chip 왼쪽 마커 (= iconKey, getStyle 에서 주입) */
  marker?: ChipMarkerKey;
  /** 시간 지정 chip / 어젠다에서 아이콘 text 색 (종일 막대는 흰색 상속) */
  markerColor: string;
  /** 일정의 카테고리 (i18n 라벨 구하기 위함) */
  category: EventCategory;
}

/**
 * 카테고리별 chip 색 — Brand 중심 매핑 (2026-06-01)
 *
 * 원칙:
 *  - primary 오렌지는 "내 채널" 한 그룹에만 (brand 시각 보호)
 *  - 전사는 red (중요·공지)
 *  - 개인은 emerald (본인 영역, 차분하지만 시인성)
 *  - task 는 sky (액션/마감, primary 와 hue 분리)
 *  - received 는 primary 의 옅은 변형 + dashed (같은 그룹 시각 호환성)
 *  - holiday 는 rose (분홍, 시스템)
 *  - invited 는 violet (RSVP 별도 트랙)
 */
/** chip 왼쪽 카테고리 마커 아이콘 키 — 모든 카테고리에 표시 */
export type ChipMarkerKey = 'lock' | 'hash' | 'mail' | 'building' | 'calendar' | 'shared' | 'list-todo';

export function eventChipStyle(category: EventCategory): EventChipStyle {
  switch (category) {
    case 'private':
      return {
        filledBg: 'bg-emerald-500 text-white',
        dot: 'bg-emerald-500',
        softBg: 'bg-emerald-500/10',
        iconKey: 'lock',
        markerColor: 'text-emerald-500',
        category: 'private',
      };
    case 'private_shared':
      return {
        filledBg: 'bg-emerald-500 text-white',
        dot: 'bg-emerald-500',
        softBg: 'bg-emerald-500/10',
        iconKey: 'lock',
        markerColor: 'text-emerald-500',
        category: 'private_shared',
      };
    case 'channel_mine':
      return {
        filledBg: 'bg-primary text-primary-foreground',
        dot: 'bg-primary',
        softBg: 'bg-primary/10',
        iconKey: 'hash',
        markerColor: 'text-primary',
        category: 'channel_mine',
      };
    case 'channel_shared':
      return {
        filledBg: 'bg-primary/60 text-white',
        dot: 'bg-primary/60',
        softBg: 'bg-primary/5',
        borderExtra: 'border-dashed border-primary/50',
        iconKey: 'shared',
        markerColor: 'text-primary/70',
        category: 'channel_shared',
      };
    case 'received':
      return {
        filledBg: 'bg-primary/40 text-white',
        dot: 'bg-primary/50',
        softBg: 'bg-primary/5',
        borderExtra: 'border-dashed border-primary/40',
        iconKey: 'shared',
        markerColor: 'text-primary/70',
        category: 'received',
      };
    case 'invited':
      return {
        filledBg: 'bg-violet-500 text-white',
        dot: 'bg-violet-500',
        softBg: 'bg-violet-500/10',
        iconKey: 'mail',
        markerColor: 'text-violet-500',
        category: 'invited',
      };
    case 'workspace':
      return {
        filledBg: 'bg-admin text-white',
        dot: 'bg-admin',
        softBg: 'bg-admin/10',
        iconKey: 'building',
        markerColor: 'text-admin',
        category: 'workspace',
      };
    case 'holiday':
      return {
        filledBg: 'bg-red-500 text-white',
        dot: 'bg-red-500',
        softBg: 'bg-red-500/10',
        iconKey: 'calendar',
        markerColor: 'text-red-500',
        category: 'holiday',
      };
    case 'task':
      return {
        filledBg: 'bg-sky-500 text-white',
        dot: 'bg-sky-500',
        softBg: 'bg-sky-500/10',
        borderExtra: 'border-l-4 border-sky-500',
        iconKey: 'list-todo',
        markerColor: 'text-sky-500',
        category: 'task',
      };
  }
}

/** chip 왼쪽 마커 — 모든 카테고리에 카테고리별 아이콘 표시 */
export function chipMarker(category: EventCategory): ChipMarkerKey {
  return eventChipStyle(category).iconKey;
}

export function categorizeEvent(
  event: Pick<CalendarEvent, 'visibility' | 'owner_id' | 'owner_channel_id' | 'shared_channel_ids' | 'metadata'>,
  myUserId: string,
  myChannelIds: Set<string>,
  isAttendee: boolean,
  currentChannelId?: string,
): EventCategory {
  if (event.metadata?.__holiday) return 'holiday';
  if (event.metadata?.__task) return 'task';
  if (event.visibility === 'public_workspace') return 'workspace';

  const isMine = event.owner_id === myUserId;
  const hasOwnerChannel = !!event.owner_channel_id;

  // 채널 캘린더 컨텍스트: owner_channel_id 기준으로 단순 분기
  // (멤버 여부와 무관 — "이 채널이 owner 인가" vs "다른 채널이 owner 인가" 두 가지)
  if (currentChannelId && hasOwnerChannel) {
    if (event.owner_channel_id === currentChannelId) return 'channel_mine';
    return 'channel_shared';
  }

  if (isMine && !hasOwnerChannel) {
    return event.shared_channel_ids.length > 0 ? 'private_shared' : 'private';
  }
  if (hasOwnerChannel && myChannelIds.has(event.owner_channel_id!)) {
    return 'channel_mine';
  }
  if (isAttendee) return 'invited';
  return 'received';
}

// ─── 메인 인터페이스 ────────────────────────────────────────

export interface CalendarEvent {
  id: string;
  workspace_id: string;
  owner_id: string;

  // 가시성 / 공개 대상
  visibility: EventVisibility;
  /** 채널 소유권. null=개인 일정 (메인 캘린더에서 만든). 값 있음=채널 일정 (그 채널 안에서 만든) */
  owner_channel_id: string | null;
  shared_channel_ids: string[];  // owner_channel_id 외 추가 공유 채널

  title: string;
  description: string | null;
  location: string | null;
  meeting_link: string | null;      // Phase 2
  room_call_id: string | null;      // Phase 2

  start_at: string;                 // ISO timestamp
  end_at: string;
  is_all_day: boolean;
  timezone: string;

  recurrence_rule: string | null;   // Step 6

  /** @deprecated per-user 모델로 이전 (2026-06-03). 리마인더는 event_reminders 테이블 (사용자별).
   *  더 이상 쓰지 않음 — fetchMyReminders / setMyReminders 사용. Phase 2 에 컬럼 제거 예정. */
  reminders: number[];

  creator: EventAuthor;
  attachments: any[];

  status: EventStatus;

  color: string | null;             // event 색상 override

  cross_refs: any[];
  source_post_id: string | null;
  source_board_id: string | null;
  is_auto_generated: boolean;

  scheduled_task_ids: string[] | null;
  comment_count: number;
  metadata: Record<string, any> | null;
  created_at: string;
  updated_at: string | null;
  archived_at: string | null;
}

/** Supabase Row → CalendarEvent */
export function rowToEvent(row: any): CalendarEvent {
  return {
    id: row.id,
    workspace_id: row.workspace_id,
    owner_id: row.owner_id ?? '',

    visibility: (row.visibility ?? 'private') as EventVisibility,
    owner_channel_id: row.owner_channel_id ?? null,
    shared_channel_ids: Array.isArray(row.shared_channel_ids) ? row.shared_channel_ids : [],

    title: row.title ?? '',
    description: row.description ?? null,
    location: row.location ?? null,
    meeting_link: row.meeting_link ?? null,
    room_call_id: row.room_call_id ?? null,

    start_at: row.start_at,
    end_at: row.end_at,
    is_all_day: !!row.is_all_day,
    timezone: row.timezone ?? 'UTC',

    recurrence_rule: row.recurrence_rule ?? null,

    reminders: Array.isArray(row.reminders) ? row.reminders.filter((n: any) => typeof n === 'number') : [],

    creator: (row.creator as EventAuthor) ?? { uid: row.owner_id ?? '', name: '' },
    attachments: Array.isArray(row.attachments) ? row.attachments : [],

    status: (row.status ?? 'confirmed') as EventStatus,
    color: row.color ?? null,

    cross_refs: Array.isArray(row.cross_refs) ? row.cross_refs : [],
    source_post_id: row.source_post_id ?? null,
    source_board_id: row.source_board_id ?? null,
    is_auto_generated: !!row.is_auto_generated,

    scheduled_task_ids: Array.isArray(row.scheduled_task_ids) ? row.scheduled_task_ids : null,
    comment_count: row.comment_count ?? 0,
    metadata: (row.metadata as Record<string, any>) ?? null,
    created_at: row.created_at ?? new Date().toISOString(),
    updated_at: row.updated_at ?? null,
    archived_at: row.archived_at ?? null,
  };
}

// ─── EventAttendee ──────────────────────────────────────────

export interface EventAttendee {
  event_id: string;
  user_id: string;
  status: AttendeeStatus;
  is_organizer: boolean;
  responded_at: string | null;
}

export function rowToAttendee(row: any): EventAttendee {
  return {
    event_id: row.event_id,
    user_id: row.user_id,
    status: (row.status ?? 'pending') as AttendeeStatus,
    is_organizer: !!row.is_organizer,
    responded_at: row.responded_at ?? null,
  };
}

// ─── EventException ─────────────────────────────────────────

export interface EventException {
  id: string;
  event_id: string;
  recurrence_id: string;
  is_cancelled: boolean;
  override: Record<string, any>;
  created_at: string;
}

export function rowToException(row: any): EventException {
  return {
    id: row.id,
    event_id: row.event_id,
    recurrence_id: row.recurrence_id,
    is_cancelled: !!row.is_cancelled,
    override: (row.override as Record<string, any>) ?? {},
    created_at: row.created_at ?? new Date().toISOString(),
  };
}

// ─── EventNotification ──────────────────────────────────────

export interface EventNotification {
  id: string;
  recipient_user_id: string;
  workspace_id: string;
  event_id: string;
  type: EventNotificationType;
  actor_user_id: string | null;
  payload: Record<string, any>;
  is_read: boolean;
  created_at: string;
}

export function rowToNotification(row: any): EventNotification {
  return {
    id: row.id,
    recipient_user_id: row.recipient_user_id,
    workspace_id: row.workspace_id,
    event_id: row.event_id,
    type: row.type as EventNotificationType,
    actor_user_id: row.actor_user_id ?? null,
    payload: (row.payload as Record<string, any>) ?? {},
    is_read: !!row.is_read,
    created_at: row.created_at ?? new Date().toISOString(),
  };
}

// ─── 공휴일 ────────────────────────────────────────────────

export interface PlatformHoliday {
  country_code: string;
  year: number;
  date: string;
  name: string;
  name_en: string | null;
  type: 'public' | 'bank' | 'optional';
  source: 'date-holidays' | 'manual';
}

export function rowToHoliday(row: any): PlatformHoliday {
  return {
    country_code: row.country_code,
    year: row.year,
    date: row.date,
    name: row.name,
    name_en: row.name_en ?? null,
    type: (row.type ?? 'public') as PlatformHoliday['type'],
    source: (row.source ?? 'date-holidays') as PlatformHoliday['source'],
  };
}

export interface CustomHoliday {
  date: string;
  name: string;
  recurrence?: 'yearly';
}

export interface WorkspaceHolidayConfig {
  workspace_id: string;
  country_codes: string[];
  custom_holidays: CustomHoliday[];
  updated_at: string;
}

export function rowToHolidayConfig(row: any): WorkspaceHolidayConfig {
  return {
    workspace_id: row.workspace_id,
    country_codes: Array.isArray(row.country_codes) ? row.country_codes : [],
    custom_holidays: Array.isArray(row.custom_holidays) ? row.custom_holidays : [],
    updated_at: row.updated_at ?? new Date().toISOString(),
  };
}

// ─── 유틸리티 ──────────────────────────────────────────────

export function eventsOverlap(a: CalendarEvent, b: CalendarEvent): boolean {
  return a.start_at < b.end_at && b.start_at < a.end_at;
}
